// /groups/:groupId/calendar — a month grid.
//
// The subtle part: which CELL an event lands in.
//
// `eventDate` is naive wall-clock in the event's authoring zone. A 10 PM event
// authored in New York is 7 PM the same day for a Los Angeles viewer, but an
// 11 PM one is 8 PM — and a 1 AM one is the PREVIOUS day. So the cell has to be
// chosen from the event's instant converted into the viewer's zone, not from the
// stored date string. Bucketing on `eventDate` directly is the bug this comment
// exists to prevent.
//
// The month window queried from the server is widened by a day on each side for
// exactly that reason: an event stored on the 1st can display on the last day of
// the previous month.

import { ApiClientError, fetchGroup, listEvents, listMyEvents } from '../api/client.js';
import { navigate, type View } from '../router.js';
import { el } from '../dom.js';
import {
  eventDayKey,
  eventType,
  fmtEventDateTime,
  GroupRole,
  type EventSummary,
  type MyEventSummary,
} from '@msc/shared';
import { viewerZone } from '../viewerZone.js';
import { eventsViewToggle, todayInZone } from './events.js';
import { groupRunsFasts, groupShowsLeaderboard, groupSubnav } from './groupNav.js';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/**
 * Serves BOTH `/groups/:groupId/calendar` and the site-wide `/calendar`.
 *
 * One view rather than two, because the hard part — deciding which cell an event
 * lands in — is identical and is the last logic that should ever exist in two
 * copies. Only the data source, the heading, and the month links differ.
 */
export const calendarView: View = async (outlet, ctx) => {
  // No :groupId in the path means the site-wide calendar across every group.
  const siteWide = ctx.params.groupId === undefined;
  const groupId = siteWide ? 0 : Number(ctx.params.groupId);
  const main = el('main', { class: 'page' }, [el('p', { class: 'muted' }, 'Loading')]);
  outlet.append(main);

  if (!siteWide && (!Number.isInteger(groupId) || groupId <= 0)) {
    main.replaceChildren(el('p', { class: 'form-error' }, 'Invalid group.'));
    return;
  }

  const base = siteWide ? '/calendar' : `/groups/${groupId}/calendar`;
  const zone = await viewerZone();

  // ?month=YYYY-MM, defaulting to the viewer's current month.
  const requested = ctx.query.get('month') ?? '';
  const month = /^\d{4}-\d{2}$/.test(requested) ? requested : todayInZone(zone).slice(0, 7);
  const [year, monthNo] = [Number(month.slice(0, 4)), Number(month.slice(5, 7))];

  // Widened by a day either side: zone conversion can pull an event into an
  // adjacent month's grid.
  const window = {
    from: addDays(`${month}-01`, -1),
    to: addDays(lastDayOf(year, monthNo), 1),
    limit: 200,
  };

  let events: EventSummary[] | MyEventSummary[];
  let groupName = '';
  let canManageGroup = false;
  let runsFasts = false;
  let showsBoard = true;
  let canManageEvents = false;
  try {
    if (siteWide) {
      events = await listMyEvents(window);
    } else {
      const [group, rows] = await Promise.all([fetchGroup(groupId), listEvents(groupId, window)]);
      groupName = group.name;
      canManageGroup = group.myRole >= GroupRole.ADMIN;
      runsFasts = groupRunsFasts(group.allowedEventTypes);
      showsBoard = groupShowsLeaderboard(group.allowedEventTypes);
      canManageEvents = group.myRole >= GroupRole.EVENTS_MANAGER;
      events = rows;
    }
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 401) {
      navigate('/login', true);
      return;
    }
    main.replaceChildren(el('p', { class: 'form-error' }, 'Could not load the calendar.'));
    return;
  }

  // Bucket by the day the event DISPLAYS on. eventDayKey() applies the same rule
  // the card's date label uses, so a card can never show one date while sitting
  // in another day's cell: a timed event converts into the viewer's zone, an
  // all-day event stays on its stored date.
  const byDay = new Map<string, EventSummary[]>();
  for (const e of events) {
    const key = eventDayKey(e.eventDate, e.startTime, e.timeZone, zone);
    if (!key) continue;
    const list = byDay.get(key);
    if (list) list.push(e);
    else byDay.set(key, [e]);
  }

  main.replaceChildren(
    el('p', {}, [
      siteWide
        ? el('a', { href: '/events' }, 'Back to all events')
        : el('a', { href: `/groups/${groupId}` }, `Back to ${groupName}`),
    ]),
    ...(siteWide ? [] : [groupSubnav(groupId, 'events', canManageGroup, runsFasts, showsBoard)]),
    el('div', { class: 'page-head' }, [
      el('h1', {}, monthLabel(year, monthNo)),
      el('p', { class: 'muted' }, `Times shown in ${zone}`),
    ]),
    ...(siteWide ? [] : [eventsViewToggle(groupId, 'calendar', canManageEvents)]),
    el('nav', { class: 'subnav' }, [
      el('a', { href: `${base}?month=${shiftMonth(month, -1)}` }, 'Previous'),
      el('a', { href: base }, 'This month'),
      el('a', { href: `${base}?month=${shiftMonth(month, 1)}` }, 'Next'),
    ]),
    grid(year, monthNo, byDay, zone, todayInZone(zone)),
  );
};

function grid(
  year: number,
  monthNo: number,
  byDay: Map<string, EventSummary[]>,
  zone: string,
  today: string,
): HTMLElement {
  const first = `${pad4(year)}-${pad2(monthNo)}-01`;
  const daysInMonth = Number(lastDayOf(year, monthNo).slice(8));

  // Monday-first. getUTCDay() is safe here because `first` is parsed as a plain
  // UTC date, with no local-zone shifting involved.
  const jsDow = new Date(`${first}T00:00:00Z`).getUTCDay();
  const lead = (jsDow + 6) % 7;

  const cells: HTMLElement[] = [];
  for (let i = 0; i < lead; i++) cells.push(el('div', { class: 'cal-cell cal-empty' }));

  for (let day = 1; day <= daysInMonth; day++) {
    const date = `${pad4(year)}-${pad2(monthNo)}-${pad2(day)}`;
    const entries = byDay.get(date) ?? [];

    const cell = el('div', { class: `cal-cell${date === today ? ' cal-today' : ''}` }, [
      el('span', { class: 'cal-day' }, String(day)),
    ]);

    for (const e of entries) {
      const def = eventType(e.eventType);
      const time = e.startTime
        ? fmtEventDateTime(e.eventDate, e.startTime, e.timeZone, zone, 'h:mm a')
        : 'All day';
      // On the site-wide calendar a bare title is ambiguous across groups, and a
      // cell is too narrow to spell it out — so the group rides in the tooltip.
      const group = 'groupName' in e ? ` · ${(e as MyEventSummary).groupName}` : '';
      const link = el(
        'a',
        {
          class: `cal-event${e.closedAt ? ' cal-closed' : ''}`,
          href: `/events/${e.eventId}`,
          title: `${e.title} · ${time}${group}`,
        },
        `${def.icon} ${time} ${e.title}`,
      );
      cell.append(link);
    }

    cells.push(cell);
  }

  return el('div', { class: 'calendar' }, [
    ...WEEKDAYS.map((d) => el('div', { class: 'cal-head' }, d)),
    ...cells,
  ]);
}

// --- Plain date maths -----------------------------------------------------
//
// All of these treat a 'yyyy-MM-dd' as a calendar label with no zone attached,
// which is what it is. Anything that needs a real moment goes through the shared
// timezone helpers instead.

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}
function pad4(n: number): string {
  return String(n).padStart(4, '0');
}

function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function lastDayOf(year: number, monthNo: number): string {
  // Day 0 of the following month is the last day of this one.
  const d = new Date(Date.UTC(year, monthNo, 0));
  return d.toISOString().slice(0, 10);
}

function shiftMonth(month: string, delta: number): string {
  const year = Number(month.slice(0, 4));
  const monthNo = Number(month.slice(5, 7));
  const total = year * 12 + (monthNo - 1) + delta;
  return `${pad4(Math.floor(total / 12))}-${pad2((total % 12) + 1)}`;
}

function monthLabel(year: number, monthNo: number): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, monthNo - 1, 1)));
}
